
import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  Clock,
  Calendar,
  Banknote,
  HardDrive,
  FileText,
  Settings,
  Palmtree,
  MapPin,
  ShieldCheck,
  Contact,
  UserCog,
  CalendarClock,
  Users2,
  Upload,
  ChevronDown,
  ChevronRight,
  DoorOpen,
  Calculator,
  FileUp,
  Briefcase,
  Smartphone,
  Download,
  ScanFace,
  UtensilsCrossed,
  ChefHat,
  Users
} from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { Role } from '../types';
import settingsService, { CompanySettings } from '../services/settingsService';
import api from '../services/api';

const Sidebar = () => {
  const { currentUser } = useAppContext();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [dataImportOpen, setDataImportOpen] = useState(false);
  const [mealOpen, setMealOpen] = useState(false);
  const [companyInfo, setCompanyInfo] = useState<CompanySettings | null>(null);
  // Customer feature flags — backend /api/customer-features endpoint'inden tek seferde gelir.
  // Default: meal=true (geriye uyumlu), sites=false. Backend cevap verirse override.
  // 2026-08-27 (Gonuc: "mobil yok, appsettings'te false yaptim ama menude duruyor"):
  // Backend /api/customer-features zaten mobile bayragini donduruyordu ama
  // burada YALNIZCA sites ve meal okunuyordu; mobil blogu kosulsuz ciziliyordu.
  const [features, setFeatures] = useState<{ sites: boolean; meal: boolean; mobile: boolean }>(
    { sites: false, meal: true, mobile: true });

  useEffect(() => {
    loadCompanyInfo();
    loadFeatures();
  }, []);

  const loadCompanyInfo = async () => {
    try {
      const settings = await settingsService.getAll();
      setCompanyInfo(settings.company);
    } catch (err) {
      console.error('Company info load error:', err);
    }
  };

  const loadFeatures = async () => {
    try {
      const res: any = await api.get('/customer-features');
      if (res.success && res.data) setFeatures({
        sites: !!res.data.sites,
        meal: res.data.meal !== false,
        // Eski backend bu alani gondermiyorsa (undefined) mobil GORUNUR kalir —
        // mevcut musterilerde menu kaybolmasin diye geriye uyumlu.
        mobile: res.data.mobile !== false,
      });
    } catch {
      // Eski backend (endpoint yok) -> default: meal=true, sites=false
    }
  };

  const sitesEnabled = features.sites;
  const mealEnabled = features.meal;
  const mobileEnabled = features.mobile;

  const isAdmin = currentUser.role === Role.ADMIN;
  const isManager = currentUser.role === Role.MANAGER;
  const isDeptManager = currentUser.role === Role.DEPARTMENT_MANAGER;

  // Ana menü öğeleri - DEPARTMENT_MANAGER sadece Puantaj ve Raporlar'ı görebilir
  const navItems = [
    { to: '/', label: 'Gösterge Paneli', icon: LayoutDashboard, roles: [Role.ADMIN, Role.MANAGER, Role.USER] as Role[] },
    { to: '/employees', label: 'Personel Listesi', icon: Contact, roles: [Role.ADMIN, Role.MANAGER, Role.USER] as Role[] },
    { to: '/devices', label: 'PDKS Cihazları', icon: HardDrive, roles: [Role.ADMIN, Role.MANAGER] as Role[] },
    { to: '/attendance-logs', label: 'Giriş/Çıkış Kayıtları', icon: DoorOpen, roles: [Role.ADMIN, Role.MANAGER, Role.USER] as Role[] },
    { to: '/timesheet', label: 'Puantaj Hesaplama', icon: Calculator, roles: [Role.ADMIN, Role.MANAGER, Role.DEPARTMENT_MANAGER] as Role[] },
    { to: '/payroll', label: 'Bordro ve Maaş', icon: Banknote, roles: [Role.ADMIN] as Role[] },
    { to: '/reports', label: 'Raporlar', icon: FileText, roles: [Role.ADMIN, Role.MANAGER, Role.DEPARTMENT_MANAGER] as Role[] },
    // 2026-08-15 (kullanici talebi): NFC Giris/Cikis menuden kaldirildi.
    // Sayfa ve rotasi DURUYOR — NFC operatoru rolu acilista buraya yonlendiriliyor
    // ve adres elle yazilarak da erisilebiliyor. Yalnizca menude gorunmuyor.
  ];

  // Veri Aktarımı altındaki menü öğeleri
  const dataImportItems = [
    { to: '/import', label: 'Personel Import', icon: Upload, roles: [Role.ADMIN] },
    { to: '/attendance-import', label: 'Puantaj Import', icon: FileUp, roles: [Role.ADMIN, Role.MANAGER] },
  ];

  // Yemekhane altındaki menü öğeleri
  const mealItems = [
    { to: '/meal-dashboard', label: 'Yemekhane Paneli', icon: LayoutDashboard, roles: [Role.ADMIN, Role.MANAGER] },
    { to: '/meal-personnel', label: 'Yemekhane Personeli', icon: Users, roles: [Role.ADMIN, Role.MANAGER] },
    { to: '/meal-transactions', label: 'Yemek Hareketleri', icon: DoorOpen, roles: [Role.ADMIN, Role.MANAGER] },
    { to: '/meal-menus', label: 'Yemek Menüleri', icon: ChefHat, roles: [Role.ADMIN, Role.MANAGER] },
    { to: '/meal-periods', label: 'Öğün Tanımları', icon: Clock, roles: [Role.ADMIN] },
    { to: '/meal-entitlements', label: 'Yemek Hakları', icon: Users, roles: [Role.ADMIN, Role.MANAGER] },
    { to: '/meal-devices', label: 'Yemekhane Cihazları', icon: HardDrive, roles: [Role.ADMIN] },
    { to: '/meal-catering', label: 'Yemek Firmaları', icon: Building2, roles: [Role.ADMIN] },
    { to: '/meal-reports', label: 'Yemekhane Raporları', icon: FileText, roles: [Role.ADMIN, Role.MANAGER] },
  ];

  // Ayarlar altındaki menü öğeleri
  const settingsItems = [
    { to: '/users', label: 'Kullanıcı Yönetimi', icon: UserCog, roles: [Role.ADMIN] },
    { to: '/permissions', label: 'Yetki Yönetimi', icon: ShieldCheck, roles: [Role.ADMIN, Role.MANAGER] },
    ...(sitesEnabled ? [{ to: '/sites', label: 'Şantiyeler', icon: MapPin, roles: [Role.ADMIN, Role.MANAGER] }] : []),
    { to: '/organization', label: 'Departman Yönetimi', icon: Building2, roles: [Role.ADMIN, Role.MANAGER] },
    { to: '/locations', label: 'Lokasyonlar', icon: MapPin, roles: [Role.ADMIN] },
    { to: '/positions', label: 'Pozisyonlar', icon: Briefcase, roles: [Role.ADMIN, Role.MANAGER] },
    { to: '/shifts', label: 'Vardiyalar ve Program', icon: Clock, roles: [Role.ADMIN, Role.MANAGER] },
    { to: '/workgroups', label: 'Çalışma Grupları', icon: Users2, roles: [Role.ADMIN, Role.MANAGER] },
    { to: '/leaves', label: 'İzin Yönetimi', icon: Calendar, roles: [Role.ADMIN, Role.MANAGER, Role.USER] },
    { to: '/leave-types', label: 'İzin Türleri', icon: Calendar, roles: [Role.ADMIN, Role.MANAGER] },
    { to: '/holidays', label: 'Resmi Tatiller', icon: Palmtree, roles: [Role.ADMIN, Role.MANAGER, Role.USER] },
    { to: '/settings', label: 'Sistem Ayarları', icon: Settings, roles: [Role.ADMIN] },
  ];

  const visibleNavItems = navItems.filter(item => {
    if (!item.roles) return true;
    return item.roles.includes(currentUser.role);
  });

  const visibleDataImportItems = dataImportItems.filter(item => {
    if (!item.roles) return true;
    return item.roles.includes(currentUser.role);
  });

  const visibleSettingsItems = settingsItems.filter(item => {
    if (!item.roles) return true;
    return item.roles.includes(currentUser.role);
  });

  const visibleMealItems = mealEnabled ? mealItems.filter(item => {
    if (!item.roles) return true;
    return item.roles.includes(currentUser.role);
  }) : [];

  return (
    <aside className="w-64 bg-slate-900 text-white h-screen flex flex-col fixed left-0 top-0 z-30 transition-all">
      <div className="h-16 flex-shrink-0 flex items-center px-4 border-b border-slate-800">
        {(() => {
          // 2026-08-10: Sifir kurulumda Settings.CompanyName seed '{{COMPANY_NAME}}' placeholder olarak kalabilir.
          // Bu string'i gecerli firma adi sayma — NexVera PDKS'e dus.
          const rawName = companyInfo?.CompanyName ?? '';
          const isPlaceholder = /^\{\{.*\}\}$/.test(rawName.trim()) || rawName.trim().length === 0;
          const displayName = isPlaceholder ? '' : rawName;

          if (companyInfo?.Logo && displayName) {
            return (
              <div className="flex items-center gap-3">
                <img src={companyInfo.Logo} alt={displayName} className="h-10 w-10 object-contain rounded-lg bg-white p-1" />
                <div className="font-bold text-sm text-white truncate max-w-[140px]">{displayName}</div>
              </div>
            );
          }
          if (displayName) {
            return <div className="font-bold text-lg text-white truncate">{displayName}</div>;
          }
          // Fallback: NexVera PDKS
          return <div className="font-bold text-xl tracking-wider text-blue-400">NexVera<span className="text-white">PDKS</span></div>;
        })()}
      </div>

      <div className="px-4 py-4 flex-shrink-0">
        <div className="bg-slate-800/50 rounded-2xl p-3 flex items-center gap-3 border border-slate-700/50">
           <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-[10px] font-black uppercase ${isAdmin ? 'bg-red-500 text-white shadow-lg shadow-red-900/20' : isManager ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/10' : isDeptManager ? 'bg-purple-600 text-white shadow-lg shadow-purple-900/10' : 'bg-slate-700 text-slate-400'}`}>
              {isAdmin ? 'ADM' : isManager ? 'YNT' : isDeptManager ? 'DPT' : 'KUL'}
           </div>
           <div>
              <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Yetki Seviyesi</div>
              <div className="text-xs font-bold text-white mt-1 capitalize">{isAdmin ? 'Yönetici' : isManager ? 'Yönetici' : isDeptManager ? 'Departman Yöneticisi' : 'Standart'} Mod</div>
           </div>
        </div>
      </div>

      <nav className="flex-1 py-2 space-y-1 px-3 sidebar-scrollbar overflow-y-auto">
        {visibleNavItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
                isActive
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/20'
                  : 'text-slate-400 hover:bg-slate-800 hover:text-white'
              }`
            }
          >
            <item.icon size={18} />
            <span className="font-bold text-xs uppercase tracking-widest">{item.label}</span>
          </NavLink>
        ))}

        {/* Veri Aktarımı Bölümü - Accordion */}
        {visibleDataImportItems.length > 0 && (
          <div className="mt-2">
            <button
              onClick={() => setDataImportOpen(!dataImportOpen)}
              className={`flex items-center justify-between w-full px-4 py-3 rounded-xl transition-all duration-300 ${
                dataImportOpen
                  ? 'bg-slate-800 text-white'
                  : 'text-slate-400 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <div className="flex items-center gap-3">
                <FileUp size={18} />
                <span className="font-bold text-xs uppercase tracking-widest">Veri Aktarımı</span>
              </div>
              {dataImportOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>

            {dataImportOpen && (
              <div className="mt-1 ml-2 space-y-1 border-l border-slate-700 pl-2">
                {visibleDataImportItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-300 ${
                        isActive
                          ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/20'
                          : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                      }`
                    }
                  >
                    <item.icon size={16} />
                    <span className="font-medium text-[10px] uppercase tracking-wider">{item.label}</span>
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Yemekhane Bölümü - Accordion */}
        {visibleMealItems.length > 0 && (
          <div className="mt-2">
            <button
              onClick={() => setMealOpen(!mealOpen)}
              className={`flex items-center justify-between w-full px-4 py-3 rounded-xl transition-all duration-300 ${
                mealOpen
                  ? 'bg-slate-800 text-white'
                  : 'text-slate-400 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <div className="flex items-center gap-3">
                <UtensilsCrossed size={18} />
                <span className="font-bold text-xs uppercase tracking-widest">Yemekhane</span>
              </div>
              {mealOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>

            {mealOpen && (
              <div className="mt-1 ml-2 space-y-1 border-l border-slate-700 pl-2">
                {visibleMealItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-300 ${
                        isActive
                          ? 'bg-orange-600 text-white shadow-lg shadow-orange-900/20'
                          : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                      }`
                    }
                  >
                    <item.icon size={16} />
                    <span className="font-medium text-[10px] uppercase tracking-wider">{item.label}</span>
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Ayarlar Bölümü - Accordion */}
        {visibleSettingsItems.length > 0 && (
          <div className="mt-4">
            <button
              onClick={() => setSettingsOpen(!settingsOpen)}
              className={`flex items-center justify-between w-full px-4 py-3 rounded-xl transition-all duration-300 ${
                settingsOpen
                  ? 'bg-slate-800 text-white'
                  : 'text-slate-400 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <div className="flex items-center gap-3">
                <Settings size={18} />
                <span className="font-bold text-xs uppercase tracking-widest">Ayarlar</span>
              </div>
              {settingsOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>

            {settingsOpen && (
              <div className="mt-1 ml-2 space-y-1 border-l border-slate-700 pl-2">
                {visibleSettingsItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-300 ${
                        isActive
                          ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/20'
                          : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                      }`
                    }
                  >
                    <item.icon size={16} />
                    <span className="font-medium text-[10px] uppercase tracking-wider">{item.label}</span>
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        )}

        {/* NexVera Mobil — QR Davet + APK indirme (Customer:Features:Mobile) */}
        {mobileEnabled && (
        <div className="mt-6 pt-4 border-t border-slate-800 space-y-2">
          <NavLink
            to="/mobile-invite"
            className={({ isActive }) =>
              `group flex items-center gap-3 px-4 py-3 rounded-xl border transition-all ${
                isActive
                  ? 'bg-gradient-to-br from-amber-600/40 to-amber-900/20 border-amber-500/60'
                  : 'bg-gradient-to-br from-amber-600/20 to-amber-900/10 border-amber-700/30 hover:from-amber-600/30 hover:to-amber-900/20'
              }`
            }
          >
            <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-amber-600/20 flex items-center justify-center">
              <Smartphone size={18} className="text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-[11px] text-amber-300 uppercase tracking-widest leading-tight">
                NexVera Mobil
              </div>
              <div className="text-[10px] text-amber-200/60 truncate">
                QR davet ve APK indirme
              </div>
            </div>
          </NavLink>

          <NavLink
            to="/mobile-logs"
            className={({ isActive }) =>
              `flex items-center gap-2 px-4 py-2 rounded-xl transition-colors ${
                isActive
                  ? 'bg-slate-800 text-amber-300'
                  : 'text-slate-400 hover:bg-slate-800 hover:text-amber-300'
              }`
            }
          >
            <DoorOpen size={14} />
            <span className="text-[10px] font-semibold uppercase tracking-wider">Mobil Kayıtlar</span>
          </NavLink>

          <NavLink
            to="/mobile-settings"
            className={({ isActive }) =>
              `flex items-center gap-2 px-4 py-2 rounded-xl transition-colors ${
                isActive
                  ? 'bg-slate-800 text-amber-300'
                  : 'text-slate-400 hover:bg-slate-800 hover:text-amber-300'
              }`
            }
          >
            <Settings size={14} />
            <span className="text-[10px] font-semibold uppercase tracking-wider">Mobil Ayarlar</span>
          </NavLink>

          <a
            href="/downloads/nexvera-pdks.apk"
            download="nexvera-pdks.apk"
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-slate-400 hover:bg-slate-800 hover:text-amber-300 transition-colors"
          >
            <Download size={14} />
            <span className="text-[10px] font-semibold uppercase tracking-wider">APK Direkt İndir</span>
          </a>
        </div>
        )}
      </nav>
    </aside>
  );
};

export default Sidebar;
